import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cx } from './cx';
import { Spinner } from './Spinner';

/**
 * The ONE button. Every button in the app routes through this - there are no
 * bespoke per-component button styles in the design system.
 *
 * - variant: primary (ink fill) · secondary (outline) · soft (quiet bone fill)
 *   · ghost · accent (the pulse)
 * - tone: default · danger (destructive: bid cancel, delete, remove) - composes
 *   with any variant, pulling the --danger token instead of ink/accent.
 * - size: sm · md  (compact LOOK; ≥44px HIT AREA on coarse/touch pointers per SPEC §4)
 * - loading: disable + show a spinner WITHOUT collapsing width (children stay,
 *   just hidden, so the box doesn't jump)
 * - fullWidth: stretch to container
 * - as: render a different element (e.g. react-router Link) while keeping styles
 *
 * Padding is PILL-PROPORTIONED: generous horizontal inset, tighter vertical
 * (sm `px-3 py-1`, md `px-4 py-1.5`), matched to the `.pill-action` surface (the
 * Contact pill etc.) so every button reads as the same rounded pill, never a
 * cramped square. Height stays content-driven (text size is fixed per size and
 * `leading-none` keeps the line-box tight), so a row of same-size buttons still
 * lines up. Do NOT switch back to equal `p-*` - the side padding read too tight.
 *
 * Touch target: the VISIBLE box keeps its pill padding on every device. The
 * >=44px tap-target minimum is met by a transparent centered ::after overlay on
 * coarse pointers - it extends the clickable area WITHOUT changing the painted
 * box. (Earlier this used min-height:44px, which stretched the button taller on
 * touch and broke the padding - the bug this comment now prevents.)
 *
 * Disabled is a FLAT bone treatment (not opacity): all disabled buttons read
 * identically inert regardless of variant, instead of a faded-but-clickable look.
 *
 * Shape: every button is a full pill (`rounded-full`) - the one button shape,
 * site-wide. Icon-only buttons round to a circle; fullWidth buttons read as a
 * stadium. There is no square/rectangular button variant.
 */
type Variant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'accent';
type Tone = 'default' | 'danger';
type Size = 'sm' | 'md';

const BASE = cx(
  'relative inline-flex items-center justify-center gap-2 select-none',
  'font-body font-bold uppercase tracking-[0.1em] leading-none whitespace-nowrap',
  'border transition-[background-color,border-color,color,opacity,transform] duration-150 ease-out',
  'cursor-pointer',
  // Flat, variant-agnostic disabled: clearly inert, never a faded-but-live look.
  'disabled:bg-bone disabled:text-text-faint disabled:border-bone disabled:cursor-not-allowed disabled:pointer-events-none',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'motion-safe:active:scale-[0.985]',
  // Touch target only (coarse pointers): a transparent centered overlay extends
  // the *clickable* area to >=44px. The painted box keeps its pill padding -
  // do NOT swap this back to min-height, which stretches the button vertically.
  "[@media(pointer:coarse)]:after:content-[''] [@media(pointer:coarse)]:after:absolute",
  '[@media(pointer:coarse)]:after:left-1/2 [@media(pointer:coarse)]:after:top-1/2',
  '[@media(pointer:coarse)]:after:-translate-x-1/2 [@media(pointer:coarse)]:after:-translate-y-1/2',
  '[@media(pointer:coarse)]:after:h-full [@media(pointer:coarse)]:after:w-full',
  '[@media(pointer:coarse)]:after:min-h-[44px] [@media(pointer:coarse)]:after:min-w-[44px]',
);

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-ink text-bg border-ink hover:bg-text hover:border-text',
  secondary: 'bg-surface text-ink border-border hover:bg-bone hover:border-text-muted',
  // Quiet, low-emphasis filled button: bone fill with a matching (invisible)
  // border at rest; on hover a faint outline appears. Reads softer than a
  // primary/accent without disappearing like ghost. (Promoted from the old
  // profile-only treatment so it's reusable app-wide.)
  soft: 'bg-bone text-ink border-bone hover:border-text-muted',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-bone',
  accent: 'bg-accent text-accent-ink border-accent hover:brightness-95',
};

// Destructive tone - same shapes as the variants, but pulling the danger token.
const DANGER_VARIANTS: Record<Variant, string> = {
  primary: 'bg-danger text-bg border-danger hover:brightness-110',
  secondary: 'bg-surface text-danger border-danger-border hover:bg-danger-bg hover:border-danger',
  soft: 'bg-danger-bg text-danger border-danger-bg hover:border-danger',
  ghost: 'bg-transparent text-danger border-transparent hover:bg-danger-bg',
  accent: 'bg-danger text-bg border-danger hover:brightness-110',
};

// Pill-proportioned padding: generous horizontal, tighter vertical - matched to
// the .pill-action surface (the Contact pill: --space-1 / --space-3 = 4px/12px)
// so every button reads as the same rounded pill, never a cramped square. md is
// a touch roomier. Do NOT revert to equal `p-*` (the side padding was too tight).
const SIZES: Record<Size, string> = {
  sm: 'px-3 py-1 text-[0.75rem]',
  md: 'px-4 py-1.5 text-[0.8125rem]',
};

type ButtonOwnProps = {
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Polymorphic: defaults to <button>, but `as` can render any element/component
 * (e.g. react-router's Link) and the element's own props (like `to`) typecheck.
 */
export type ButtonProps<C extends ElementType = 'button'> = ButtonOwnProps & {
  as?: C;
} & Omit<ComponentPropsWithoutRef<C>, keyof ButtonOwnProps | 'as'>;

export function Button<C extends ElementType = 'button'>({
  variant = 'primary',
  tone = 'default',
  size = 'md',
  loading = false,
  fullWidth = false,
  as,
  className,
  children,
  ...rest
}: ButtonProps<C>) {
  const Tag = (as ?? 'button') as ElementType;
  const variantCls = (tone === 'danger' ? DANGER_VARIANTS : VARIANTS)[variant];
  // Loading locks interaction WITHOUT the native `disabled` attribute - disabled
  // would trigger the flat bone treatment, but a loading button should keep its
  // variant colour under the spinner. aria-busy/aria-disabled + pointer-events
  // + removing it from the tab order blocks both mouse and keyboard activation.
  const loadingAttrs = loading
    ? { 'aria-busy': true, 'aria-disabled': true, tabIndex: -1 }
    : null;

  return (
    <Tag
      className={cx(
        BASE,
        variantCls,
        SIZES[size],
        'rounded-full',
        fullWidth && 'w-full',
        loading && 'pointer-events-none',
        className,
      )}
      {...loadingAttrs}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </span>
      )}
      <span className={cx('inline-flex items-center gap-2', loading && 'invisible')}>{children}</span>
    </Tag>
  );
}
